import React, { useEffect } from "react";
import { FormField } from "../inputField/textfield";
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AddButton } from "../button/button";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store/appStore";
import { getVendorList } from "../../redux/slices/vendorList";
import { useParams } from "react-router-dom";
import { getProject } from "../../redux/slices/projectSlice/addProjectSlice";
import { getReview } from "../../redux/slices/reviewerSlice";

interface VendorunallocationBodyProps {
  formDataArray: Array<{
    vendor_id: string;
    task_count: string;
    type: string;
    status: string;
    review_stage_id?: any;
  }>;
  setFormDataArray: React.Dispatch<
    React.SetStateAction<
      Array<{
        vendor_id: string;
        task_count: string;
        type: string;
        status: string;
        review_stage_id?: any;
      }>
    >
  >;
}

const VendorUnallocationBody: React.FC<VendorunallocationBodyProps> = ({
  formDataArray,
  setFormDataArray,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();

  const vendorList = useSelector(
    (state: RootState) => state.vendorList.vendor?.results || []
  );
  const reviewList = useSelector(
    (state: RootState) => state.reviewStage.reviewer || []
  );

  useEffect(() => {
    dispatch(getVendorList({ paginate: true, project_id: id || "" }));
    dispatch(getProject({ project_id: id || "" }));
    dispatch(getReview({ project_id: id }));
  }, [id, dispatch]);

  const handleInputChange = (
    e: { target: { name: string; value: string } },
    index: number
  ) => {
    const { name, value } = e.target;
    const newFormDataArray = [...formDataArray];
    newFormDataArray[index] = {
      ...newFormDataArray[index],
      [name]: name === "task_count" ? Number(value) : value,
    };
    setFormDataArray(newFormDataArray);
  };

  const handleSelectFieldChange = (
    event: SelectChangeEvent<string>,
    index: number,
    fieldName: string
  ) => {
    const value = event.target.value;

    setFormDataArray((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        const updatedItem: any = { ...item, [fieldName]: value };

        // if (fieldName === "type") {
        //   if (value === "TASKING") {
        //     updatedItem.type = "TASKING";
        //     delete updatedItem.review_stage_id;
        //   } else {
        //     updatedItem.review_stage_id = value;
        //     updatedItem.type = "REVIEWING";
        //   }
        // }

        return updatedItem;
      })
    );
  };

  const getVendorPermissions = (vendorId: string) => {
    const selectedVendor = vendorList.find(
      (vendor: any) => vendor.vendor_id === vendorId
    );
    if (!selectedVendor)
      return {
        taskingAllowed: false,
        reviewingAllowed: false,
        bothAllowed: false,
      };

    const permissions = selectedVendor.permissions || {};
    const tasking =
      permissions.task_allocation_single?.[0] ||
      permissions.task_allocation_bulk?.[0];
    const reviewing =
      permissions.review_unallocation_single?.[0] ||
      permissions.review_unallocation_bulk?.[0];
    const both = tasking && reviewing;

    return {
      taskingAllowed: tasking,
      reviewingAllowed: reviewing,
      bothAllowed: both,
    };
  };

  const handleSelectStatusChange = (
    event: SelectChangeEvent<string[]>,
    index: number,
    fieldName: string
  ) => {
    const selectedArray = event.target.value as string[];
    const selectedString = selectedArray.join(", ");

    const newDataArr = [...formDataArray];
    newDataArr[index] = {
      ...newDataArr[index],
      [fieldName]: selectedString,
    };

    setFormDataArray(newDataArr);
  };

  const handleAddMore = () => {
    setFormDataArray([
      ...formDataArray,
      {
        vendor_id: "",
        task_count: "",
        type: "",
        status: "",
      },
    ]);
  };

  const handleDelete = (index: number) => {
    const newFormDataArray = formDataArray.filter((_, i) => i !== index);
    setFormDataArray(newFormDataArray);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
  };

  const statusOptions = [
    { value: "NOT_STARTED", label: "Not Started" },
    { value: "NOT_ALLOCATED", label: "Not Allocated" },
  ];

  const renderReviewOptions = () =>
    reviewList?.results?.map((review: any) => (
      <MenuItem key={review.id} value={review.id}>
        {review.name}
      </MenuItem>
    ));

  return (
    <div
      style={{
        padding: "10px",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <form onSubmit={handleSubmit}>
        {formDataArray.map((formData, index) => {
          const permissions = getVendorPermissions(formData.vendor_id);

          return (
            <div
              key={index}
              style={{
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexGrow: 1,
                }}
              >
                {/* Vendor */}
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  marginRight: "20px",
                  width: "32%",
                }}>
                  <Typography sx={{
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "24px",
                    textAlign: "left",
                    color: "#10383D",
                  }}> Select Vendor </Typography>
                  <Select
                    name="vendor"
                    value={formData.vendor_id}
                    onChange={(e) =>
                      handleSelectFieldChange(e, index, "vendor_id")
                    }
                    size="small"
                    displayEmpty
                    sx={{ width: "220px" }}
                  >
                    <MenuItem value="" disabled>
                      Select Vendor
                    </MenuItem>
                    {vendorList
                      .map((a: any) => ({
                        ...a,
                        vendor_pseudo_name_small:
                          a.pseudo_name?.toLowerCase() ||
                          a.vendor_pseudo_name?.toLowerCase(),
                      }))
                      .filter((a) => a.vendor_id)
                      .sort((a, b) =>
                        a.vendor_pseudo_name_small < b.vendor_pseudo_name_small
                          ? -1
                          : a.vendor_pseudo_name_small >
                            b.vendor_pseudo_name_small
                            ? 1
                            : 0
                      )
                      .map((vendor: any) => (
                        <MenuItem
                          key={vendor.vendor_id}
                          value={vendor.vendor_id}
                          disabled={vendor.is_blocked}
                        >
                          {vendor.vendor_pseudo_name ||
                            vendor.pseudo_name ||
                            vendor.vendor_name}
                        </MenuItem>
                      ))}
                  </Select>
                </div>

                {/* Type */}
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  marginRight: "20px",
                  width: "32%",
                }}>
                  <Typography sx={{
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "24px",
                    textAlign: "left",
                    color: "#10383D",
                  }}> Select Stage </Typography>
                  <Select
                    name="type"
                    value={Number(formData.type)}
                    onChange={(e: any) => handleSelectFieldChange(e, index, "type")}
                    sx={{ width: "220px" }}
                    size="small"
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select Stage
                    </MenuItem>
                    {reviewList?.results?.map((review: any) => (
                      <MenuItem key={review.id} value={review.id}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          width="100%"
                          flexWrap="wrap"
                        >
                          <Typography
                            variant="body2"
                            sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {review.name}
                          </Typography>
                          <Chip
                            label={review.category || "Review"}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{
                              fontSize: "0.75rem",
                              height: 22,
                              ml: 1,
                              mt: { xs: 0.5, sm: 0 },
                            }}
                          />
                        </Box>
                      </MenuItem>

                    ))}
                  </Select>
                </div>

                {/* Status */}
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  marginRight: "20px",
                  width: "32%",
                }}>
                  <Typography sx={{
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "24px",
                    textAlign: "left",
                    color: "#10383D",
                  }}> Status </Typography>
                  <Select
                    name="status"
                    value={
                      formData.status ? formData.status.split(", ") : []
                    }
                    onChange={(e) => handleSelectStatusChange(e, index, "status")}
                    sx={{ width: "220px" }}
                    size="small"
                    multiple
                    renderValue={(selected: string[]) =>
                      selected
                        .map(
                          (val) =>
                            statusOptions.find((opt) => opt.value === val)
                              ?.label || val
                        )
                        .join(", ")
                    }
                  >
                    <MenuItem value="" disabled>
                      Select Status
                    </MenuItem>
                    {statusOptions.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>
                        <Checkbox
                          checked={
                            formData.status?.split(", ").includes(value) || false
                          }
                          size="small"
                        />
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                {/* Task Count */}
                <FormField
                  label="Task Count"
                  placeholder="Enter Task Count"
                  name="task_count"
                  value={formData.task_count}
                  onChange={(e) => handleInputChange(e, index)}
                  textFieldSx={{ width: "220px" }}
                />
                <Tooltip title={index === 0 ? "Cannot delete first item" : "Delete"}>
                  <span>
                    <IconButton
                      onClick={index === 0 ? undefined : () => handleDelete(index)}
                      disabled={index === 0}
                      sx={{
                        marginLeft: "10px",
                        color: "#ec480f",
                        marginTop: "28px",
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>
          );
        })}
        <AddButton onClick={handleAddMore}>Add More</AddButton>
      </form>
    </div>
  );
};

export default VendorUnallocationBody;